import { CareOSLoader } from '@/components/ui/careos-loader';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from 'next-themes';
import { useTranslation } from 'react-i18next';
import { useLandingPlans } from '@/hooks/useLandingPlans';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Heart,
  Shield,
  Eye,
  Clock,
  CheckCircle2,
  Users,
  Smartphone,
  AlertTriangle,
  FileCheck,
  Sparkles,
  Loader2,
  ClipboardList,
  Bell,
  BarChart3,
  CalendarClock,
  UserPlus,
  Lock,
  ArrowRight,
  Sun,
  Moon,
  Globe,
  FileText,
  Activity,
  Package,
  MapPin,
  Mic,
  ChevronDown,
  ShieldCheck,
  BookOpen,
  HelpCircle,
  Layers,
  Monitor,
  UserCheck,
  MessageCircle,
  Mail
} from 'lucide-react';
import { ThemedLogo } from '@/components/ui/themed-logo';
import { SEO } from '@/components/SEO';
import { useEffect, useRef, useState } from 'react';
import { motion, type Variants } from 'framer-motion';
import { track } from '@/lib/analytics';
import { useLandingFunnelMetrics, useSectionViewed } from '@/hooks/useLandingFunnelMetrics';
import heroMedicationsImg from '@/assets/landing/hero-medications.jpg';
import securityShieldImg from '@/assets/landing/security-shield.jpg';
import caregiverPatientImg from '@/assets/landing/caregiver-patient.jpg';

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const staggerContainer: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};

const viewportOnce = { once: true, amount: 0.2 } as const;

export default function LandingPage() {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language?.startsWith('pt') ? 'pt-BR' : 'en';
  const toggleLanguage = () => i18n.changeLanguage(currentLanguage === 'pt-BR' ? 'en' : 'pt-BR');
  const navigate = useNavigate();
  const { user } = useAuth();
  const { setTheme, resolvedTheme } = useTheme();
  const { familyPrice, familyYearlyNote, companyTiers, companyStartingAt, isLoading: plansLoading } = useLandingPlans();
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const heroRef = useRef<HTMLElement>(null);
  const familiesRef = useRef<HTMLElement>(null);
  const companiesRef = useRef<HTMLElement>(null);
  const howItWorksRef = useRef<HTMLElement>(null);
  const pricingRef = useRef<HTMLElement>(null);
  const faqRef = useRef<HTMLElement>(null);
  const finalCtaRef = useRef<HTMLElement>(null);

  useLandingFunnelMetrics();
  useSectionViewed(heroRef, 'hero');
  useSectionViewed(familiesRef, 'families');
  useSectionViewed(companiesRef, 'companies');
  useSectionViewed(howItWorksRef, 'how_it_works');
  useSectionViewed(pricingRef, 'pricing', 'pricing_viewed');
  useSectionViewed(faqRef, 'faq');
  useSectionViewed(finalCtaRef, 'final_cta');

  useEffect(() => {
    track('landing_viewed', {
      referrer: typeof document !== 'undefined' ? document.referrer : undefined,
      language: currentLanguage,
    });
  }, [currentLanguage]);

  const goToSignup = (audience: 'b2c' | 'b2b', cta_id: string, location: string) => {
    track('cta_clicked', { cta_id, location, audience, destination: '/auth?mode=signup' });
    track('signup_started', { audience, source: location });
    navigate('/auth?mode=signup');
  };

  const trackPlanClick = (plan: 'free' | 'family' | 'company', audience: 'b2c' | 'b2b') => {
    track('cta_clicked', { cta_id: `pricing_${plan}`, location: 'pricing', audience, destination: '/auth?mode=signup' });
    track('pricing_plan_clicked', { plan_id: plan, audience, billing_period: 'monthly' });
    track('signup_started', { audience, source: `pricing_${plan}` });
    navigate('/auth?mode=signup');
  };

  const trackCta = (cta_id: string, location: string, destination: string, extra: Record<string, unknown> = {}) => {
    track('cta_clicked', { cta_id, location, destination, ...extra });
  };


  const supportWhatsApp = import.meta.env.VITE_SUPPORT_WHATSAPP || '';
  const supportEmail = import.meta.env.VITE_SUPPORT_EMAIL || 'help@careos.life';

  if (user) {
    navigate('/app');
    return (
      <CareOSLoader fullScreen label={t('common.loading')} />
    );
  }

  const painPoints = [
    { icon: AlertTriangle, titleKey: 'landing.painPoints.items.noInfo.title', descKey: 'landing.painPoints.items.noInfo.description' },
    { icon: FileText, titleKey: 'landing.painPoints.items.paper.title', descKey: 'landing.painPoints.items.paper.description' },
    { icon: Heart, titleKey: 'landing.painPoints.items.anxiety.title', descKey: 'landing.painPoints.items.anxiety.description' },
    { icon: Users, titleKey: 'landing.painPoints.items.noControl.title', descKey: 'landing.painPoints.items.noControl.description' },
  ];

  const familyFeatures = [
    { icon: Eye, titleKey: 'landing.forFamilies.features.realtime.title', descKey: 'landing.forFamilies.features.realtime.description' },
    { icon: Sparkles, titleKey: 'landing.forFamilies.features.ai.title', descKey: 'landing.forFamilies.features.ai.description' },
    { icon: Bell, titleKey: 'landing.forFamilies.features.alerts.title', descKey: 'landing.forFamilies.features.alerts.description' },
    { icon: BarChart3, titleKey: 'landing.forFamilies.features.reports.title', descKey: 'landing.forFamilies.features.reports.description' },
    { icon: Activity, titleKey: 'landing.forFamilies.features.vitals.title', descKey: 'landing.forFamilies.features.vitals.description' },
    { icon: Package, titleKey: 'landing.forFamilies.features.inventory.title', descKey: 'landing.forFamilies.features.inventory.description' },
  ];

  const companyFeatures = [
    { icon: Monitor, titleKey: 'landing.forCompanies.features.dashboard.title', descKey: 'landing.forCompanies.features.dashboard.description' },
    { icon: CalendarClock, titleKey: 'landing.forCompanies.features.shifts.title', descKey: 'landing.forCompanies.features.shifts.description' },
    { icon: UserPlus, titleKey: 'landing.forCompanies.features.invite.title', descKey: 'landing.forCompanies.features.invite.description' },
    { icon: Lock, titleKey: 'landing.forCompanies.features.access.title', descKey: 'landing.forCompanies.features.access.description' },
    { icon: BarChart3, titleKey: 'landing.forCompanies.features.analytics.title', descKey: 'landing.forCompanies.features.analytics.description' },
    { icon: Mic, titleKey: 'landing.forCompanies.features.handoff.title', descKey: 'landing.forCompanies.features.handoff.description' },
  ];

  const steps = [
    { number: '1', titleKey: 'landing.howItWorks.steps.register.title', descKey: 'landing.howItWorks.steps.register.description', icon: ClipboardList },
    { number: '2', titleKey: 'landing.howItWorks.steps.carePlan.title', descKey: 'landing.howItWorks.steps.carePlan.description', icon: Sparkles },
    { number: '3', titleKey: 'landing.howItWorks.steps.invite.title', descKey: 'landing.howItWorks.steps.invite.description', icon: UserPlus },
    { number: '4', titleKey: 'landing.howItWorks.steps.track.title', descKey: 'landing.howItWorks.steps.track.description', icon: Eye },
  ];

  const stats = [
    { valueKey: 'landing.stats.taskTypesValue', labelKey: 'landing.stats.taskTypes', descKey: 'landing.stats.taskTypesDesc' },
    { valueKey: 'landing.stats.vitalSignsValue', labelKey: 'landing.stats.vitalSigns', descKey: 'landing.stats.vitalSignsDesc' },
    { valueKey: 'landing.stats.rolesValue', labelKey: 'landing.stats.roles', descKey: 'landing.stats.rolesDesc' },
    { valueKey: 'landing.stats.platformsValue', labelKey: 'landing.stats.platforms', descKey: 'landing.stats.platformsDesc' },
  ];

  const securityFeatures = [
    { icon: ShieldCheck, titleKey: 'landing.security.features.encryption.title', descKey: 'landing.security.features.encryption.description' },
    { icon: Lock, titleKey: 'landing.security.features.rbac.title', descKey: 'landing.security.features.rbac.description' },
    { icon: BookOpen, titleKey: 'landing.security.features.audit.title', descKey: 'landing.security.features.audit.description' },
  ];

  const faqItems = t('landing.faq.items', { returnObjects: true }) as Array<{ question: string; answer: string }>;

  const seoTitle = currentLanguage === 'pt-BR'
    ? 'CareOS — Gestão Inteligente de Cuidado Domiciliar'
    : 'CareOS — Intelligent Home Care Management';
  const seoDescription = currentLanguage === 'pt-BR'
    ? 'Plataforma de home care para famílias e empresas: plano de cuidados com IA, escala de cuidadores, sinais vitais, check-in com localização e relatórios em tempo real.'
    : 'Home care platform for families and agencies: AI care plans, caregiver scheduling, vital signs, location-based check-in and real-time reports.';

  const offers: any[] = [];
  if (familyPrice) {
    offers.push({
      "@type": "Offer",
      "name": "Plano Família",
      "price": familyPrice.replace(/[^\d,]/g, '').replace(',', '.'),
      "priceCurrency": "BRL",
      "category": "subscription",
      "url": "https://careos.life/auth?mode=signup",
    });
  }
  if (companyStartingAt) {
    offers.push({
      "@type": "Offer",
      "name": "Plano Empresas",
      "price": companyStartingAt.replace(/[^\d,]/g, '').replace(',', '.'),
      "priceCurrency": "BRL",
      "category": "subscription",
      "url": "https://careos.life/auth?mode=signup",
      "description": "Preço por paciente; escalonado por volume.",
    });
  }

  const softwareJsonLd = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "@id": "https://careos.life/#software",
    "name": "CareOS",
    "applicationCategory": "HealthApplication",
    "operatingSystem": "Android, Web",
    "url": "https://careos.life/",
    "description": seoDescription,
    "inLanguage": ["pt-BR", "en"],
    "publisher": { "@id": "https://careos.life/#organization" },
    ...(offers.length > 0
      ? { "offers": offers }
      : { "offers": { "@type": "Offer", "price": "0", "priceCurrency": "BRL" } }),
  };

  const faqJsonLd = Array.isArray(faqItems) && faqItems.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map((item) => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": { "@type": "Answer", "text": item.answer },
    })),
  } : null;

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://careos.life/" },
    ],
  };

  const jsonLdGraphs = [softwareJsonLd, breadcrumbJsonLd, ...(faqJsonLd ? [faqJsonLd] : [])];

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={seoTitle}
        description={seoDescription}
        canonical="https://careos.life/"
        lang={currentLanguage}
        alternates={[
          { hreflang: 'pt-BR', href: 'https://careos.life/' },
          { hreflang: 'en', href: 'https://careos.life/' },
          { hreflang: 'x-default', href: 'https://careos.life/' },
        ]}
        jsonLd={jsonLdGraphs}
      />

      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-border">
        <div className="container max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ThemedLogo className="h-10 w-10 object-contain" />
            <span className="font-bold text-xl text-primary">CareOS</span>
          </div>
          <nav aria-label="Navegação principal" className="hidden md:flex items-center gap-1">
            {[
              { href: '#families', label: t('landing.forFamilies.badge'), id: 'nav_families' },
              { href: '#companies', label: t('landing.forCompanies.badge'), id: 'nav_companies' },
              { href: '#how-it-works', label: t('landing.howItWorks.title'), id: 'nav_how_it_works' },
              { href: '#pricing', label: t('landing.pricing.title'), id: 'nav_pricing' },
              { href: '#faq', label: 'FAQ', id: 'nav_faq' },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => trackCta(link.id, 'header_nav', link.href)}
                className="px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-md"
              >
                {link.label}
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full"
              onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
            >
              <Sun className="h-[1.2rem] w-[1.2rem] rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
              <Moon className="absolute h-[1.2rem] w-[1.2rem] rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
              <span className="sr-only">Toggle theme</span>
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full"
              onClick={toggleLanguage}
            >
              <Globe className="h-[1.2rem] w-[1.2rem]" />
              <span className="sr-only">Toggle language</span>
            </Button>
            <Button variant="ghost" size="sm" onClick={() => { trackCta('header_login', 'header', '/auth'); navigate('/auth'); }}>
              {t('auth.login')}
            </Button>
          </div>
        </div>
      </header>

      <main id="main-content">
      {/* Hero */}
      <section ref={heroRef} aria-labelledby="hero-heading" className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-accent/20" />
        <div className="container max-w-6xl mx-auto px-4 py-16 md:py-24 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              className="text-center lg:text-left max-w-2xl mx-auto lg:mx-0"
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
            >
              <motion.div
                variants={fadeUp}
                className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6"
              >
                <CheckCircle2 className="w-4 h-4" />
                {t('landing.hero.badge')}
              </motion.div>
              <motion.h1
                id="hero-heading"
                variants={fadeUp}
                className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight"
              >
                {t('landing.hero.titlePart1')}{' '}
                <span className="text-primary">{t('landing.hero.titlePart2')}</span>
              </motion.h1>
              <motion.p variants={fadeUp} className="text-lg md:text-xl text-muted-foreground mb-4">
                {t('landing.hero.description')}
              </motion.p>
              <motion.p variants={fadeUp} className="text-base text-muted-foreground/80 mb-8 italic">
                {t('landing.hero.footer')}
              </motion.p>

              <motion.div variants={fadeUp} className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center lg:items-start justify-center lg:justify-start gap-4 mb-8 w-full">
                <Button
                  size="lg"
                  className="text-base lg:text-lg px-6 lg:px-8 h-14 rounded-full shadow-lg hover:shadow-xl transition-all w-full sm:w-auto"
                  onClick={() => goToSignup('b2c', 'hero_family', 'hero')}
                >
                  <Heart className="mr-2 w-5 h-5" />
                  {t('landing.hero.ctaFamily')}
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="text-base lg:text-lg px-6 lg:px-8 h-14 rounded-full w-full sm:w-auto"
                  onClick={() => goToSignup('b2b', 'hero_company', 'hero')}
                >
                  <Users className="mr-2 w-5 h-5" />
                  {t('landing.hero.ctaCompany')}
                </Button>
              </motion.div>

              <motion.p variants={fadeUp} className="text-xs text-muted-foreground/60">
                {t('landing.hero.trustedBy')}
              </motion.p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, ease: 'easeOut', delay: 0.2 }}
              className="relative lg:order-last w-full max-w-xl mx-auto lg:max-w-none"
            >
              <div aria-hidden className="absolute inset-0 bg-primary/20 blur-3xl rounded-full opacity-50" />
              <div className="relative rounded-3xl overflow-hidden shadow-2xl ring-1 ring-border/50 aspect-[4/5]">
                <img
                  src={heroMedicationsImg}
                  alt={currentLanguage === 'pt-BR' ? 'Aplicativo CareOS sobre uma mesa, ao lado de receitas médicas e caixas de remédio — substituindo o controle em papel.' : 'CareOS app on a table next to handwritten prescriptions and medication boxes — replacing paper-based care.'}
                  className="w-full h-full object-cover"
                  fetchPriority="high"
                  loading="eager"
                  width={1280}
                  height={1600}
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-primary/10 via-transparent to-transparent pointer-events-none" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Microcopy intro for crawlers / LLMs */}
      <section aria-label="Sobre o CareOS" className="py-8 bg-background border-b border-border/50">
        <div className="container max-w-3xl mx-auto px-4 text-center">
          <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
            {currentLanguage === 'pt-BR'
              ? 'O CareOS é uma plataforma completa de gestão de cuidado domiciliar (home care) para famílias e empresas. Oferece plano de cuidados gerado por inteligência artificial a partir da receita médica, escala de plantões para cuidadores, monitoramento de sinais vitais (pressão, glicemia, saturação, frequência cardíaca, temperatura, peso), check-in com localização, controle de medicamentos e relatórios automáticos diários e semanais.'
              : 'CareOS is a complete home care management platform for families and agencies. It offers AI-generated care plans from medical prescriptions, caregiver shift scheduling, vital signs monitoring (blood pressure, glucose, oxygen saturation, heart rate, temperature, weight), location-based check-in, medication inventory and automated daily and weekly reports.'}
          </p>
        </div>
      </section>
      <section className="py-16 bg-destructive/5">
        <div className="container max-w-5xl mx-auto px-4">
          <motion.div
            className="text-center mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            variants={fadeUp}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t('landing.painPoints.title')}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t('landing.painPoints.description')}
            </p>
          </motion.div>
          <motion.div
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            variants={staggerContainer}
          >
            {painPoints.map((p, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                className="text-center p-6 rounded-xl bg-background border border-border shadow-sm"
              >
                <div className="w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
                  <p.icon className="w-6 h-6 text-destructive" />
                </div>
                <h3 className="font-semibold mb-2 text-foreground">{t(p.titleKey)}</h3>
                <p className="text-muted-foreground text-sm">{t(p.descKey)}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Para Famílias */}
      <section ref={familiesRef} id="families" className="py-16 bg-card scroll-mt-16">
        <div className="container max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Heart className="w-4 h-4" />
              {t('landing.forFamilies.badge')}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t('landing.forFamilies.title')}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t('landing.forFamilies.description')}
            </p>
          </div>
          <motion.div
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            variants={staggerContainer}
          >
            {familyFeatures.map((f, i) => (
              <motion.div key={i} variants={fadeUp}>
                <Card className="hover:shadow-lg transition-shadow border-t-4 border-t-primary/30 h-full">
                  <CardContent className="pt-6">
                    <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                      <f.icon className="w-7 h-7 text-primary" />
                    </div>
                    <h3 className="font-semibold mb-2">{t(f.titleKey)}</h3>
                    <p className="text-muted-foreground text-sm">{t(f.descKey)}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
          <div className="text-center mt-8">
            <Button
              size="lg"
              className="rounded-full"
              onClick={() => navigate('/auth?mode=signup')}
            >
              {t('landing.hero.ctaFamily')}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Para Empresas */}
      <section ref={companiesRef} id="companies" className="py-16 scroll-mt-16">
        <div className="container max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
              <Shield className="w-4 h-4" />
              {t('landing.forCompanies.badge')}
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t('landing.forCompanies.title')}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t('landing.forCompanies.description')}
            </p>
          </div>
          <motion.div
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            variants={staggerContainer}
          >
            {companyFeatures.map((f, i) => (
              <motion.div key={i} variants={fadeUp}>
                <Card className="hover:shadow-lg transition-shadow border-t-4 border-t-primary/30 h-full">
                  <CardContent className="pt-6">
                    <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                      <f.icon className="w-7 h-7 text-primary" />
                    </div>
                    <h3 className="font-semibold mb-2">{t(f.titleKey)}</h3>
                    <p className="text-muted-foreground text-sm">{t(f.descKey)}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
          <div className="text-center mt-8">
            <Button
              size="lg"
              variant="outline"
              className="rounded-full"
              onClick={() => navigate('/auth?mode=signup')}
            >
              {t('landing.hero.ctaCompany')}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Stats / Numbers */}
      <section className="py-16 bg-primary/5">
        <div className="container max-w-5xl mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground text-center mb-12">
            {t('landing.stats.title')}
          </h2>
          <motion.div
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            variants={staggerContainer}
          >
            {stats.map((s, i) => (
              <motion.div key={i} variants={fadeUp} className="text-center">
                <div className="text-5xl font-bold text-primary mb-2">{t(s.valueKey)}</div>
                <div className="font-semibold text-foreground mb-1">{t(s.labelKey)}</div>
                <p className="text-xs text-muted-foreground">{t(s.descKey)}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Como Funciona */}
      <section ref={howItWorksRef} id="how-it-works" className="py-16 bg-card scroll-mt-16">
        <div className="container max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t('landing.howItWorks.title')}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t('landing.howItWorks.description')}
            </p>
          </div>
          <motion.div
            className="grid md:grid-cols-4 gap-6 max-w-4xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            variants={staggerContainer}
          >
            {steps.map((step, i) => (
              <motion.div key={i} variants={fadeUp} className="text-center relative">
                <div className="w-16 h-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center mx-auto mb-4 text-2xl font-bold shadow-lg">
                  {step.number}
                </div>
                {i < steps.length - 1 && (
                  <ArrowRight className="hidden md:block absolute top-8 -right-3 w-6 h-6 text-muted-foreground/30" />
                )}
                <h3 className="font-semibold text-base mb-2">{t(step.titleKey)}</h3>
                <p className="text-muted-foreground text-sm">{t(step.descKey)}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Pricing */}
      <section ref={pricingRef} id="pricing" className="py-16 scroll-mt-16">
        <div className="container max-w-5xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t('landing.pricing.title')}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto mb-8">
              {t('landing.pricing.description')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* Free */}
            <Card className="hover:shadow-lg transition-shadow border-2 border-transparent hover:border-border">
              <CardContent className="pt-8 text-center">
                <div className="mb-4">
                  <h3 className="text-xl font-bold">{t('landing.pricing.free.title')}</h3>
                  <div className="flex items-baseline justify-center gap-1 mt-2">
                    <span className="text-4xl font-bold">{t('landing.pricing.free.price')}</span>
                  </div>
                  <p className="text-muted-foreground mt-2 text-sm">{t('landing.pricing.free.description')}</p>
                </div>
                <ul className="space-y-3 mb-8 text-left max-w-[240px] mx-auto">
                  {(Array.isArray(t('landing.pricing.free.features', { returnObjects: true }))
                    ? t('landing.pricing.free.features', { returnObjects: true }) as string[]
                    : []).map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                </ul>
                <Button variant="outline" className="w-full" onClick={() => trackPlanClick('free', 'b2c')}>
                  {t('landing.pricing.free.cta')}
                </Button>
              </CardContent>
            </Card>

            {/* Família */}
            <Card className="hover:shadow-lg transition-shadow border-2 border-primary relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-primary text-primary-foreground text-xs font-bold px-3 py-1 rounded-bl-lg">
                {t('landing.pricing.family.badge')}
              </div>
              <CardContent className="pt-8 text-center">
                <div className="mb-4">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Heart className="w-5 h-5 text-primary" />
                    <h3 className="text-xl font-bold text-primary">{t('landing.pricing.family.title')}</h3>
                  </div>
                  <div className="flex items-baseline justify-center gap-1 mt-2">
                    {plansLoading ? (
                      <Skeleton className="h-10 w-32" />
                    ) : (
                      <span className="text-4xl font-bold">{familyPrice ?? 'R$ 79,90'}</span>
                    )}
                    <span className="text-muted-foreground">/ {t('landing.pricing.toggle.monthly').toLowerCase()}</span>
                  </div>
                  {familyYearlyNote && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {familyYearlyNote}
                    </p>
                  )}
                  <p className="text-muted-foreground mt-3 text-sm">{t('landing.pricing.family.description')}</p>
                </div>
                <ul className="space-y-3 mb-8 text-left max-w-[240px] mx-auto">
                  {(Array.isArray(t('landing.pricing.family.features', { returnObjects: true }))
                    ? t('landing.pricing.family.features', { returnObjects: true }) as string[]
                    : []).map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                </ul>
                <Button className="w-full" onClick={() => trackPlanClick('family', 'b2c')}>
                  {t('landing.pricing.family.cta')}
                </Button>
              </CardContent>
            </Card>

            {/* Empresas */}
            <Card className="hover:shadow-lg transition-shadow border-2 border-transparent hover:border-primary/30">
              <CardContent className="pt-8 text-center">
                <div className="mb-4">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Users className="w-5 h-5 text-primary" />
                    <h3 className="text-xl font-bold">{t('landing.pricing.company.title')}</h3>
                  </div>
                  <div className="flex items-baseline justify-center gap-1 mt-2">
                    <span className="text-sm text-muted-foreground mr-1">{t('landing.pricing.company.startingAt')}</span>
                    {plansLoading ? (
                      <Skeleton className="h-10 w-32" />
                    ) : (
                      <span className="text-4xl font-bold">{companyStartingAt ?? 'R$ 24,90'}</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('landing.pricing.company.perPatient')}
                  </p>

                  <div className="mt-3 bg-muted/50 rounded-lg p-3 text-sm">
                    <p className="font-semibold text-foreground mb-2">{t('landing.pricing.company.tiersTitle')}</p>
                    <div className="space-y-1 text-xs text-muted-foreground">
                      {plansLoading ? (
                        <>
                          <Skeleton className="h-4 w-full" />
                          <Skeleton className="h-4 w-full" />
                          <Skeleton className="h-4 w-full" />
                          <Skeleton className="h-4 w-full" />
                        </>
                      ) : companyTiers.length > 0 ? (
                        companyTiers.map((tier, i) => (
                          <div key={i} className="flex justify-between">
                            <span>
                              {tier.upTo === 'last'
                                ? `${tier.rangeStart}+`
                                : `${tier.rangeStart}–${tier.upTo}`
                              }{' '}{t('landing.pricing.company.patients')}
                            </span>
                            <span className="font-medium text-foreground">{tier.price}</span>
                          </div>
                        ))
                      ) : (
                        <>
                          <div className="flex justify-between"><span>1–10 {t('landing.pricing.company.patients')}</span> <span className="font-medium text-foreground">R$ 49,90</span></div>
                          <div className="flex justify-between"><span>11–30 {t('landing.pricing.company.patients')}</span> <span className="font-medium text-foreground">R$ 39,90</span></div>
                          <div className="flex justify-between"><span>31–50 {t('landing.pricing.company.patients')}</span> <span className="font-medium text-foreground">R$ 29,90</span></div>
                          <div className="flex justify-between"><span>51+ {t('landing.pricing.company.patients')}</span> <span className="font-medium text-foreground">R$ 24,90</span></div>
                        </>
                      )}
                    </div>
                  </div>

                  <p className="text-muted-foreground mt-3 text-sm">{t('landing.pricing.company.description')}</p>
                </div>
                <ul className="space-y-3 mb-8 text-left max-w-[240px] mx-auto">
                  {(Array.isArray(t('landing.pricing.company.features', { returnObjects: true }))
                    ? t('landing.pricing.company.features', { returnObjects: true }) as string[]
                    : []).map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                </ul>
                <Button variant="outline" className="w-full" onClick={() => trackPlanClick('company', 'b2b')}>
                  {t('landing.pricing.company.cta')}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Security */}
      <section className="py-16 bg-card">
        <div className="container max-w-6xl mx-auto px-4">
          <motion.div
            className="grid lg:grid-cols-2 gap-12 items-center"
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            variants={staggerContainer}
          >
            <motion.div variants={fadeUp} className="relative order-last lg:order-first">
              <div aria-hidden className="absolute -inset-6 bg-primary/15 blur-3xl rounded-full opacity-70" />
              <div className="relative rounded-2xl overflow-hidden shadow-xl ring-1 ring-border/50 aspect-[4/5] max-w-md mx-auto lg:max-w-none">
                <img
                  src={securityShieldImg}
                  alt={currentLanguage === 'pt-BR' ? 'Idosa lendo tranquilamente em casa, envolvida por um escudo digital azul representando segurança e proteção dos dados.' : 'Elderly woman reading peacefully at home, wrapped by a blue digital shield representing data protection and security.'}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  width={1280}
                  height={1600}
                />
              </div>
              <div className="absolute -bottom-3 -right-3 lg:-bottom-4 lg:-right-4 bg-background border border-border shadow-lg rounded-xl px-4 py-3 flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-primary" />
                <span className="text-sm font-semibold">LGPD</span>
              </div>
            </motion.div>

            <motion.div variants={fadeUp}>
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-4">
                <Shield className="w-4 h-4" />
                {currentLanguage === 'pt-BR' ? 'Segurança' : 'Security'}
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                {t('landing.security.title')}
              </h2>
              <p className="text-muted-foreground mb-8">
                {t('landing.security.description')}
              </p>
              <ul className="space-y-6">
                {securityFeatures.map((f, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <f.icon className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold mb-1">{t(f.titleKey)}</h3>
                      <p className="text-muted-foreground text-sm">{t(f.descKey)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section ref={faqRef} id="faq" className="py-16 scroll-mt-16">
        <div className="container max-w-3xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t('landing.faq.title')}
            </h2>
          </div>
          <div className="space-y-3">
            {Array.isArray(faqItems) && faqItems.map((item, i) => (
              <div key={i} className="border border-border rounded-lg overflow-hidden">
                <button
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span className="font-medium text-foreground pr-4">{item.question}</span>
                  <ChevronDown className={`w-5 h-5 text-muted-foreground flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-4 pb-4 text-sm text-muted-foreground">
                    {item.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section ref={finalCtaRef} className="py-16 md:py-20 bg-primary/5">
        <motion.div
          className="container max-w-6xl mx-auto px-4"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={staggerContainer}
        >
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div variants={fadeUp} className="text-center lg:text-left">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
                {t('landing.finalCta.title')}
              </h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto lg:mx-0">
                {t('landing.finalCta.description')}
              </p>
              <Button
                size="lg"
                className="text-lg px-10 h-14 rounded-full shadow-lg hover:shadow-xl transition-all"
                onClick={() => { trackCta('final_cta', 'final_cta', '/auth?mode=signup'); track('signup_started', { source: 'final_cta' }); navigate('/auth?mode=signup'); }}
              >
                {t('landing.finalCta.cta')}
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <p className="text-xs text-muted-foreground mt-4">
                {t('landing.finalCta.note')}
              </p>
            </motion.div>

            <motion.div variants={fadeUp} className="relative">
              <div aria-hidden className="absolute -inset-6 bg-primary/15 blur-3xl rounded-full opacity-70" />
              <div className="relative rounded-3xl overflow-hidden shadow-2xl ring-1 ring-border/50 aspect-[4/3] max-w-lg mx-auto lg:max-w-none">
                <img
                  src={caregiverPatientImg}
                  alt={currentLanguage === 'pt-BR' ? 'Cuidadora profissional sorrindo e segurando a mão de uma idosa, com check-in confirmado no celular ao lado.' : 'Professional caregiver smiling and holding hands with an elderly woman, with a confirmed check-in shown on a phone nearby.'}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  width={1280}
                  height={960}
                />
              </div>
            </motion.div>
          </div>
        </motion.div>
      </section>
      </main>

      {/* Footer */}
      <footer aria-label="Rodapé" className="py-8 border-t border-border">
        <div className="container max-w-5xl mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-2">
              <ThemedLogo className="h-8 w-8 object-contain" />
              <span className="font-semibold text-primary">CareOS</span>
            </div>

            {(supportWhatsApp || supportEmail) && (
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-muted-foreground">{t('landing.footer.support')}:</span>
                {supportWhatsApp && (
                  <a
                    href={`https://wa.me/${supportWhatsApp.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                  >
                    <MessageCircle className="w-4 h-4" />
                    {t('landing.footer.supportWhatsApp')}
                  </a>
                )}
                {supportEmail && (
                  <a
                    href={`mailto:${supportEmail}`}
                    className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
                  >
                    <Mail className="w-4 h-4" />
                    {t('landing.footer.supportEmail')}
                  </a>
                )}
              </div>
            )}

            <p className="text-sm text-muted-foreground text-center">
              © {new Date().getFullYear()} CareOS. {t('landing.footer.madeWithLove')}
            </p>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
            <a href="/legal/terms" className="hover:text-foreground hover:underline underline-offset-4">
              Termos de Uso
            </a>
            <span aria-hidden>·</span>
            <a href="/legal/privacy" className="hover:text-foreground hover:underline underline-offset-4">
              Política de Privacidade
            </a>
            <span aria-hidden>·</span>
            <a href="/legal/cookies" className="hover:text-foreground hover:underline underline-offset-4">
              Política de Cookies
            </a>
            <span aria-hidden>·</span>
            <a href="/legal/retention" className="hover:text-foreground hover:underline underline-offset-4">
              Política de Retenção
            </a>
            <span aria-hidden>·</span>
            <a href="/legal/security" className="hover:text-foreground hover:underline underline-offset-4">
              Política de Segurança
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
